<svelte:options runes={true} />

<script lang="ts">
  import { Rect, Size, size } from '@geom';
  import { prefs } from '@reactive';
  import { resourcesState } from '@resources/resourcesLoaded.svelte';
  import { renderLayout, type SnorfLayout } from '@snorf';
  import { FontExportMethod, SVGVisual, VisualCache } from '@visual';

  // Accept a SnorfLayout prop, with a default fallback
  let {
    layout = undefined,
    mark = undefined,
    previewSize = size(50, 50),
    cache = $bindable()
  }: {
    layout?: SnorfLayout;
    mark?: string;
    previewSize?: Size;
    cache?: VisualCache;
  } = $props();

  let svgElement: HTMLDivElement;

  // Use provided cache or create our own if none provided
  let localCache = cache ?? new VisualCache();

  $effect(() => {
    if (resourcesState.resourcesLoaded && layout) {
      performance.mark(`SnorfLayoutPreview:render:${mark}:start`);
      const colorMode = prefs.colorMode;

      // Calculate scale factor to fit the layout within the preview size
      const layoutSize = layout.bounds.size;
      const scaleX = previewSize.width / layoutSize.width;
      const scaleY = previewSize.height / layoutSize.height;
      const scaleFactor = Math.min(scaleX, scaleY); // Use uniform scaling to preserve aspect ratio

      // Calculate the scaled bounds for the visual
      const scaledWidth = layoutSize.width * scaleFactor;
      const scaledHeight = layoutSize.height * scaleFactor;

      // Center the scaled content within the preview area
      const offsetX = (previewSize.width - scaledWidth) / 2;
      const offsetY = (previewSize.height - scaledHeight) / 2;

      // Create a visual with the preview bounds (not the layout bounds)
      const previewBounds = new Rect(0, 0, previewSize.width, previewSize.height);
      const visual = new SVGVisual(colorMode, localCache, previewBounds, {
        fontExportMethod: FontExportMethod.FALLBACKS
      });

      // Apply scaling and centering transformation
      visual.withGroup(
        {
          attrs: {
            transform: `translate(${offsetX}, ${offsetY}) scale(${scaleFactor})`
          }
        },
        () => {
          // Render the layout using SNORF's render function
          renderLayout(layout, visual, { mark });
        }
      );

      const svg = visual.getSvg();
      svgElement.innerHTML = svg;
      performance.mark(`SnorfLayoutPreview:render:${mark}:end`);
      performance.measure(
        `SnorfLayoutPreview:render:${mark}`,
        `SnorfLayoutPreview:render:${mark}:start`,
        `SnorfLayoutPreview:render:${mark}:end`
      );
    }

    // Cleanup function that runs when the effect re-runs or component unmounts
    return () => {
      // Only clear the cache if we created it ourselves (not bound from parent)
      if (!cache) {
        localCache.clear();
      }
    };
  });
</script>

<style>
  .layout-preview {
    display: inline-block;
    /* Use CSS custom properties set by reactive state */
    width: var(--preview-width);
    height: var(--preview-height);
  }

  .layout-preview :global(svg) {
    /* Size the SVG to match the preview dimensions */
    width: 100%;
    height: 100%;
  }
</style>

<div
  bind:this={svgElement}
  style:--preview-width="{previewSize.width}px"
  style:--preview-height="{previewSize.height}px"
  class="layout-preview"
></div>
